// src/pages/CustomersPage.tsx
import React, { useEffect, useState, useMemo } from "react";
import { Table, Form, Button, Spinner, Dropdown } from "react-bootstrap";
import { FaTrash, FaEdit } from "react-icons/fa";
import { useGlobalContext } from "../context/globalContext";
import { useTheme } from "../context/ThemeContext";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

const CustomersPage: React.FC = () => {
  const { users, getAllUsers, updateOnlineStatus } = useGlobalContext();
  const { dark } = useTheme();

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Load users on mount + poll every 10s
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      // if (mounted) setLoading(true);
      await getAllUsers();
      if (mounted) setLoading(false);
    };
    load();
    const id = setInterval(load, 10000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter logic
  const filteredUsers = useMemo(() => {
    if (!users) return [];
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u: any) => {
      return (
        (u.firstName ?? "").toString().toLowerCase().includes(q) ||
        (u.lastName ?? "").toString().toLowerCase().includes(q) ||
        (u.email ?? "").toString().toLowerCase().includes(q) ||
        (u.username ?? "").toString().toLowerCase().includes(q)
      );
    });
  }, [users, search]);

  const statusBadge = (u: any) => {
    const lastActive = u?.lastActive ? dayjs(u.lastActive) : null;
    const minutesSince = lastActive ? dayjs().diff(lastActive, "minute") : Infinity;
    if (u?.isOnline) return <span className="badge bg-success">Online</span>;
    if (minutesSince <= 5) return <span className="badge bg-warning text-dark">Active</span>;
    return <span className="badge bg-secondary">Offline</span>;
  };

  const handleToggleOnline = async (id: string, current: boolean) => {
    await updateOnlineStatus(id, !current);
  };

  // Theme styles
  const bgClass = dark ? "bg-dark text-light" : "bg-light text-dark";
  const tableClass = dark ? "table-dark" : "table-light";
  const cardClass = dark ? "bg-secondary text-light" : "bg-white text-dark";

  return (
    <div className={`min-vh-100 p-3 ${bgClass}`}>
      <div className={`p-3 mb-4 shadow-sm rounded-4 ${cardClass}`}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="fw-bold mb-1">User Management</h4>
            <small className="text-muted">Manage users, monitor activity, and control access</small>
          </div>
          <div className="d-flex gap-2">
            <Form.Control
              type="text"
              placeholder="Search by name, email or username"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ minWidth: 280 }}
              className={dark ? "bg-dark text-light border-secondary" : ""}
            />
            <Button
              variant={dark ? "outline-light" : "outline-secondary"}
              onClick={() => getAllUsers()}
            >
              Refresh
            </Button>
            <Button variant={dark ? "light" : "dark"}>+ Add User</Button>
          </div>
        </div>
      </div>

      <div className={`shadow rounded-4 p-3 ${cardClass}`}>
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" role="status" variant={dark ? "light" : "dark"} />
          </div>
        ) : (
          <Table hover responsive className={`align-middle ${tableClass}`}>
            <thead>
              <tr>
                <th>Full Name</th>
                <th>Email</th>
                <th>Username</th>
                <th>Status</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Last Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u: any) => (
                  <tr key={u._id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <img
                          src={u.avatarUrl || "/default-avatar.png"}
                          alt={`${u.firstName ?? u.username ?? ""}`}
                          className="rounded-circle me-2"
                          style={{ width: 40, height: 40, objectFit: "cover" }}
                        />
                        <div>
                          <div className="fw-semibold">
                            {(u.firstName || "") + (u.lastName ? ` ${u.lastName}` : "") || u.username}
                          </div>
                          <small className="text-muted">{u.email}</small>
                        </div>
                      </div>
                    </td>
                    <td>{u.email}</td>
                    <td>{u.username || "-"}</td>
                    <td>{statusBadge(u)}</td>
                    <td>{u.role ?? "User"}</td>
                    <td>{u.createdAt ? dayjs(u.createdAt).format("MMM D, YYYY") : "-"}</td>
                    <td>{u.lastActive ? dayjs(u.lastActive).fromNow() : "N/A"}</td>
                    <td>
                      <div className="d-flex gap-2 align-items-center">
                        <Dropdown>
                          <Dropdown.Toggle
                            variant={dark ? "secondary" : "light"}
                            size="sm"
                          >
                            Actions
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => handleToggleOnline(u._id, !!u.isOnline)}>
                              {u.isOnline ? "Set Offline" : "Set Online"}
                            </Dropdown.Item>
                            <Dropdown.Item>Edit</Dropdown.Item>
                            <Dropdown.Item className="text-danger">Delete</Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                        <Button variant="link" className="p-0 text-primary">
                          <FaEdit />
                        </Button>
                        <Button variant="link" className="p-0 text-danger">
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default CustomersPage;
